import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AuthLink from "../../Auth/AuthLink";
import { CheckCircle2, ChevronLeft, ChevronRight, Loader2, PauseCircle, PlayCircle, RotateCcw, Volume2, XCircle } from "lucide-react";
import api from "../../../const/axios";
import siteUrl from "../../../const/const";
import { cn } from "@/lib/utils";
import { isFeatureEnabled } from "../../../services/flagsService";

type TrainerStage = "owner-select" | "runner" | "phase-pause" | "summary";
type MarkStatus = "found" | "not_found" | null;

interface FlattenedTrainerQuestion {
  questionId: number;
  phaseId: number;
  phaseName: string;
  questionLabel: string;
  notebefore?: string;
  text: string;
  comment?: string;
  rekvizit?: { text: boolean; rekvizit: string } | null;
  isRekvizitForAll?: boolean;
}

interface TrainerOwnerOption {
  ownerType: "user" | "team";
  ownerId: number;
  label: string;
}

interface TrainerItemProgress {
  questionId: number;
  phaseId: number;
  mark: MarkStatus;
  questionReadAt: string | null;
  timerCompletedAt: string | null;
  commentReadAt: string | null;
  retalkCount: number;
}

interface PackageTrainerOverlayProps {
  open: boolean;
  onClose: () => void;
  packageId: number;
  packageName: string;
  phases: any[];
  isLoggedIn: boolean;
  currentUserId: number | null;
}

const flattenQuestions = (phases: any[]) => {
  const questions: FlattenedTrainerQuestion[] = [];
  const boundaryToNextPhaseName: Record<number, string> = {};

  type Group = { phaseId: number; phaseName: string; questions: any[] };
  const groups: Group[] = [];

  (phases ?? []).forEach((phase: any) => {
    const subs = phase?.subs ?? [];
    if (subs.length > 0) {
      subs.forEach((sub: any) => {
        groups.push({
          phaseId: Number(sub?.id ?? 0),
          phaseName: `${phase?.name ?? "Raund"} / ${sub?.name ?? "Alt raund"}`,
          questions: Array.isArray(sub?.questions) ? sub.questions : [],
        });
      });
      return;
    }

    groups.push({
      phaseId: Number(phase?.id ?? 0),
      phaseName: String(phase?.name ?? "Raund"),
      questions: Array.isArray(phase?.questions) ? phase.questions : [],
    });
  });

  groups.forEach((group, groupIndex) => {
    group.questions.forEach((q: any, qIndex: number) => {
      const first = q?.values?.[0];
      if (!first || !Number.isFinite(first?.id)) return;
      questions.push({
        questionId: Number(first.id),
        phaseId: group.phaseId,
        phaseName: group.phaseName,
        questionLabel: `${qIndex + 1}`,
        notebefore: q?.notebefore ?? "",
        text: first?.text ?? "",
        comment: q?.comment ?? "",
        rekvizit: first?.rekvizit ?? null,
        isRekvizitForAll: Boolean(q?.isRekvizitForAll),
      });
    });

    const isLastGroup = groupIndex === groups.length - 1;
    if (!isLastGroup && group.questions.length > 0) {
      const boundaryQuestionIndex = questions.length - 1;
      boundaryToNextPhaseName[boundaryQuestionIndex] = groups[groupIndex + 1].phaseName;
    }
  });

  return { questions, boundaryToNextPhaseName };
};

const formatTimer = (seconds: number): string => {
  const mm = Math.floor(seconds / 60);
  const ss = seconds % 60;
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
};

const normalizeOwnerKey = (ownerType: "user" | "team", ownerId: number): string =>
  `${ownerType}:${ownerId}`;

const TTSFREE_API_URL = (process.env.REACT_APP_TTSFREE_API_URL ?? "").trim();
const TTSFREE_API_KEY = (process.env.REACT_APP_TTSFREE_API_KEY ?? "").trim();
const TTSFREE_VOICE_SERVICE = (process.env.REACT_APP_TTSFREE_VOICE_SERVICE ?? "servicebin").trim();
const TTSFREE_VOICE_ID = (process.env.REACT_APP_TTSFREE_VOICE_ID ?? "az-AZ").trim();
const TTSFREE_VOICE_SPEED = (process.env.REACT_APP_TTSFREE_VOICE_SPEED ?? "0").trim();

const PackageTrainerOverlay: React.FC<PackageTrainerOverlayProps> = ({
  open,
  onClose,
  packageId,
  packageName,
  phases,
  isLoggedIn,
  currentUserId,
}) => {
  const { questions, boundaryToNextPhaseName } = useMemo(() => flattenQuestions(phases), [phases]);
  const questionIndexById = useMemo(() => {
    const map = new Map<number, number>();
    questions.forEach((q, idx) => map.set(q.questionId, idx));
    return map;
  }, [questions]);

  const [stage, setStage] = useState<TrainerStage>("owner-select");
  const [ownerOptions, setOwnerOptions] = useState<TrainerOwnerOption[]>([]);
  const [selectedOwnerKey, setSelectedOwnerKey] = useState<string>("");
  const [ownerLoading, setOwnerLoading] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [finishLoading, setFinishLoading] = useState(false);
  const [requiresFinish, setRequiresFinish] = useState(false);
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [pendingPhaseIndex, setPendingPhaseIndex] = useState<number | null>(null);
  const [autoNextEnabled, setAutoNextEnabled] = useState(true);
  const [itemProgress, setItemProgress] = useState<Record<number, TrainerItemProgress>>({});
  const [timerSeconds, setTimerSeconds] = useState(60);
  const [timerState, setTimerState] = useState<"idle" | "running" | "completed">("idle");
  const [speaking, setSpeaking] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [errorText, setErrorText] = useState<string | null>(null);
  const [summaryResult, setSummaryResult] = useState<any | null>(null);

  const timerIntervalRef = useRef<number | null>(null);
  const autoAdvanceTimeoutRef = useRef<number | null>(null);
  const autosaveTimeoutRef = useRef<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrlRef = useRef<string | null>(null);
  const ttsAbortRef = useRef<AbortController | null>(null);
  const ttsConfigured = Boolean(TTSFREE_API_URL && TTSFREE_API_KEY);

  const selectedOwner = useMemo(() => {
    const [ownerTypeRaw, ownerIdRaw] = selectedOwnerKey.split(":");
    const ownerType = ownerTypeRaw === "team" ? "team" : "user";
    const ownerId = Number(ownerIdRaw);
    if (!Number.isFinite(ownerId) || ownerId <= 0) return null;
    return { ownerType, ownerId } as const;
  }, [selectedOwnerKey]);

  const currentQuestion = questions[currentIndex] ?? null;
  const canReadComment = timerState === "completed" && Boolean(currentQuestion?.comment?.trim());

  const stopTimer = useCallback(() => {
    if (timerIntervalRef.current !== null) {
      window.clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  }, []);

  const clearAutoAdvance = useCallback(() => {
    if (autoAdvanceTimeoutRef.current !== null) {
      window.clearTimeout(autoAdvanceTimeoutRef.current);
      autoAdvanceTimeoutRef.current = null;
    }
  }, []);

  const stopSpeech = useCallback(() => {
    if (ttsAbortRef.current) {
      ttsAbortRef.current.abort();
      ttsAbortRef.current = null;
    }
    if (audioRef.current) {
      try {
        audioRef.current.pause();
      } catch {
        // noop
      }
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      audioRef.current = null;
    }
    if (audioUrlRef.current) {
      try {
        URL.revokeObjectURL(audioUrlRef.current);
      } catch {
        // noop
      }
      audioUrlRef.current = null;
    }
    setSpeaking(false);
  }, []);

  const upsertItemProgress = useCallback((question: FlattenedTrainerQuestion, patch: Partial<TrainerItemProgress>) => {
    setItemProgress((prev) => {
      const existing = prev[question.questionId] ?? {
        questionId: question.questionId,
        phaseId: question.phaseId,
        mark: null,
        questionReadAt: null,
        timerCompletedAt: null,
        commentReadAt: null,
        retalkCount: 0,
      };
      return {
        ...prev,
        [question.questionId]: { ...existing, ...patch, phaseId: question.phaseId },
      };
    });
  }, []);

  const startTimer = useCallback((seconds = 60) => {
    stopTimer();
    clearAutoAdvance();
    setTimerSeconds(seconds);
    setTimerState("running");
    timerIntervalRef.current = window.setInterval(() => {
      setTimerSeconds((prev) => {
        if (prev <= 1) {
          stopTimer();
          setTimerState("completed");
          if (currentQuestion) {
            upsertItemProgress(currentQuestion, { timerCompletedAt: new Date().toISOString() });
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [clearAutoAdvance, currentQuestion, stopTimer, upsertItemProgress]);

  const speak = useCallback(async (text: string, onEnd?: () => void) => {
    const cleanText = text?.trim() ?? "";
    if (!cleanText) {
      onEnd?.();
      return;
    }

    if (!ttsConfigured) {
      setErrorText("TTSFree konfiqurasiya edilməyib.");
      onEnd?.();
      return;
    }

    stopSpeech();
    const abortController = new AbortController();
    ttsAbortRef.current = abortController;
    setSpeaking(true);
    setErrorText(null);

    const cleanupAudioUrl = () => {
      if (audioUrlRef.current) {
        try {
          URL.revokeObjectURL(audioUrlRef.current);
        } catch {
          // noop
        }
        audioUrlRef.current = null;
      }
    };

    const base64ToBlob = (base64: string, mimeType = "audio/mpeg") => {
      const clean = base64.includes(",") ? base64.split(",")[1] : base64;
      const binary = atob(clean);
      const len = binary.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i += 1) {
        bytes[i] = binary.charCodeAt(i);
      }
      return new Blob([bytes], { type: mimeType });
    };

    try {
      const response = await fetch(TTSFREE_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: TTSFREE_API_KEY,
          "x-api-key": TTSFREE_API_KEY,
          Authorization: `Bearer ${TTSFREE_API_KEY}`,
        },
        body: JSON.stringify({
          text: cleanText,
          voiceService: TTSFREE_VOICE_SERVICE,
          voiceID: TTSFREE_VOICE_ID,
          voiceSpeed: TTSFREE_VOICE_SPEED,
          lang: "az-AZ",
          format: "mp3",
        }),
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`TTS request failed (${response.status})`);
      }

      const contentType = (response.headers.get("content-type") ?? "").toLowerCase();
      let audioBlob: Blob | null = null;

      if (contentType.includes("audio")) {
        audioBlob = await response.blob();
      } else {
        const data = await response.json();
        const audioUrl = data?.audioUrl ?? data?.url ?? data?.audio_url ?? data?.data?.audioUrl ?? data?.data?.url;
        const audioBase64 = data?.audioBase64 ?? data?.audio ?? data?.base64 ?? data?.audioContent ?? data?.data?.audio;

        if (audioUrl && typeof audioUrl === "string") {
          const audioResponse = await fetch(audioUrl, { signal: abortController.signal });
          if (!audioResponse.ok) throw new Error("Unable to download TTS audio.");
          audioBlob = await audioResponse.blob();
        } else if (audioBase64 && typeof audioBase64 === "string") {
          const guessedMime = audioBase64.startsWith("data:audio/") ? audioBase64.split(";")[0].replace("data:", "") : "audio/mpeg";
          audioBlob = base64ToBlob(audioBase64, guessedMime);
        } else {
          throw new Error("Unsupported TTSFree response format.");
        }
      }

      if (!audioBlob || audioBlob.size === 0) {
        throw new Error("Empty TTS audio.");
      }

      cleanupAudioUrl();
      const objectUrl = URL.createObjectURL(audioBlob);
      audioUrlRef.current = objectUrl;

      const audio = new Audio(objectUrl);
      audioRef.current = audio;
      audio.onended = () => {
        setSpeaking(false);
        cleanupAudioUrl();
        onEnd?.();
      };
      audio.onerror = () => {
        setSpeaking(false);
        cleanupAudioUrl();
        setErrorText("TTS səsi oxudula bilmədi.");
        onEnd?.();
      };

      await audio.play();
    } catch {
      setSpeaking(false);
      setErrorText("TTSFree ilə səs yaradılmadı.");
      onEnd?.();
    } finally {
      if (ttsAbortRef.current === abortController) {
        ttsAbortRef.current = null;
      }
    }
  }, [stopSpeech, ttsConfigured]);

  const buildQuestionSpeechText = useCallback((question: FlattenedTrainerQuestion) => {
    const note = question.notebefore?.trim();
    const body = question.text?.replace(/\/-\//g, " ")?.trim();
    return [note, body].filter(Boolean).join(". ");
  }, []);

  const goToSummary = useCallback(() => {
    stopSpeech();
    stopTimer();
    clearAutoAdvance();
    setRequiresFinish(true);
    setStage("summary");
  }, [clearAutoAdvance, stopSpeech, stopTimer]);

  const goNext = useCallback(() => {
    if (!currentQuestion) return;
    clearAutoAdvance();

    if (currentIndex >= questions.length - 1) {
      goToSummary();
      return;
    }

    if (boundaryToNextPhaseName[currentIndex]) {
      setPendingPhaseIndex(currentIndex + 1);
      stopSpeech();
      stopTimer();
      setStage("phase-pause");
      return;
    }

    stopSpeech();
    stopTimer();
    setCurrentIndex((prev) => Math.min(questions.length - 1, prev + 1));
  }, [boundaryToNextPhaseName, clearAutoAdvance, currentIndex, currentQuestion, goToSummary, questions.length, stopSpeech, stopTimer]);

  const goPrev = useCallback(() => {
    clearAutoAdvance();
    stopSpeech();
    stopTimer();
    setCurrentIndex((prev) => Math.max(0, prev - 1));
    setStage("runner");
  }, [clearAutoAdvance, stopSpeech, stopTimer]);

  const playQuestion = useCallback((isRetalk: boolean) => {
    if (!currentQuestion) return;
    clearAutoAdvance();
    stopTimer();
    setTimerSeconds(60);
    setTimerState("idle");

    const nowIso = new Date().toISOString();
    setItemProgress((prev) => {
      const existing = prev[currentQuestion.questionId] ?? {
        questionId: currentQuestion.questionId,
        phaseId: currentQuestion.phaseId,
        mark: null as MarkStatus,
        questionReadAt: null as string | null,
        timerCompletedAt: null as string | null,
        commentReadAt: null as string | null,
        retalkCount: 0,
      };

      const next: TrainerItemProgress = {
        ...existing,
        phaseId: currentQuestion.phaseId,
        questionReadAt: nowIso,
        timerCompletedAt: null,
        retalkCount: isRetalk ? (existing.retalkCount ?? 0) + 1 : (existing.retalkCount ?? 0),
      };

      return {
        ...prev,
        [currentQuestion.questionId]: next,
      };
    });

    const text = buildQuestionSpeechText(currentQuestion);
    speak(text, () => startTimer(60));
  }, [buildQuestionSpeechText, clearAutoAdvance, currentQuestion, speak, startTimer, stopTimer]);

  const readComment = useCallback(() => {
    if (!currentQuestion || !canReadComment) return;
    upsertItemProgress(currentQuestion, { commentReadAt: new Date().toISOString() });
    speak(currentQuestion.comment ?? "");
  }, [canReadComment, currentQuestion, speak, upsertItemProgress]);

  const buildProgressPayload = useCallback(() => {
    const items = questions.map((q) => {
      const p = itemProgress[q.questionId] ?? {
        questionId: q.questionId,
        phaseId: q.phaseId,
        mark: null,
        questionReadAt: null,
        timerCompletedAt: null,
        commentReadAt: null,
        retalkCount: 0,
      };
      return {
        questionId: q.questionId,
        phaseId: q.phaseId,
        mark: p.mark ?? null,
        questionReadAt: p.questionReadAt ?? null,
        timerCompletedAt: p.timerCompletedAt ?? null,
        commentReadAt: p.commentReadAt ?? null,
        retalkCount: p.retalkCount ?? 0,
      };
    });

    return {
      currentQuestionId: currentQuestion?.questionId ?? null,
      currentPhaseId: currentQuestion?.phaseId ?? null,
      autoNextEnabled,
      items,
      lastUpdatedAt: new Date().toISOString(),
    };
  }, [autoNextEnabled, currentQuestion, itemProgress, questions]);

  const saveProgress = useCallback(async () => {
    if (!open || !selectedOwner || !sessionId) return;
    if (stage === "owner-select" || stage === "summary") return;

    setSaveStatus("saving");
    try {
      await api.put(`packages/${packageId}/trainer/progress`, JSON.stringify({
        ownerType: selectedOwner.ownerType,
        ownerId: selectedOwner.ownerId,
        currentQuestionId: currentQuestion?.questionId ?? null,
        currentPhaseId: currentQuestion?.phaseId ?? null,
        autoNextEnabled,
        progress: buildProgressPayload(),
      }));
      setSaveStatus("saved");
      window.setTimeout(() => setSaveStatus("idle"), 1200);
    } catch {
      setSaveStatus("error");
    }
  }, [autoNextEnabled, buildProgressPayload, currentQuestion, open, packageId, selectedOwner, sessionId, stage]);

  const loadSessionProgress = useCallback((session: any) => {
    const progress = session?.progress ?? null;
    const items = Array.isArray(progress?.items) ? progress.items : [];
    const mapped: Record<number, TrainerItemProgress> = {};
    items.forEach((item: any) => {
      const qid = Number(item?.questionId ?? 0);
      if (!qid) return;
      mapped[qid] = {
        questionId: qid,
        phaseId: Number(item?.phaseId ?? 0),
        mark: item?.mark === "found" || item?.mark === "not_found" ? item.mark : null,
        questionReadAt: item?.questionReadAt ?? null,
        timerCompletedAt: item?.timerCompletedAt ?? null,
        commentReadAt: item?.commentReadAt ?? null,
        retalkCount: Number(item?.retalkCount ?? 0),
      };
    });
    setItemProgress(mapped);
    setAutoNextEnabled(Boolean(progress?.autoNextEnabled ?? session?.autoNextEnabled ?? true));

    const qid = Number(progress?.currentQuestionId ?? session?.currentQuestionId ?? 0);
    if (qid && questionIndexById.has(qid)) {
      setCurrentIndex(questionIndexById.get(qid) ?? 0);
    } else {
      setCurrentIndex(0);
    }
  }, [questionIndexById]);

  const startOrResume = useCallback(async (restart: boolean) => {
    if (!selectedOwner || !isLoggedIn || !open) return;
    setSessionLoading(true);
    setErrorText(null);
    try {
      const res = await api.post(`packages/${packageId}/trainer/start`, JSON.stringify({
        ownerType: selectedOwner.ownerType,
        ownerId: selectedOwner.ownerId,
        restart,
      }));
      const session = res.data;
      setSessionId(Number(session?.id ?? 0));

      if (!restart && session?.status === "completed" && session?.result) {
        setSummaryResult(session.result);
        loadSessionProgress(session);
        setRequiresFinish(false);
        setStage("summary");
      } else {
        setSummaryResult(null);
        loadSessionProgress(session);
        setRequiresFinish(false);
        setStage("runner");
      }
    } catch (err: any) {
      setErrorText(err?.response?.data?.message ?? "Sessiya əlçatan deyil.");
    } finally {
      setSessionLoading(false);
    }
  }, [isLoggedIn, loadSessionProgress, open, packageId, selectedOwner]);

  const finishSession = useCallback(async () => {
    if (!selectedOwner) return;
    setFinishLoading(true);
    const items = questions.map((q) => {
      const p = itemProgress[q.questionId] ?? {
        mark: null,
        questionReadAt: null,
        timerCompletedAt: null,
        commentReadAt: null,
        retalkCount: 0,
      };
      return {
        questionId: q.questionId,
        phaseId: q.phaseId,
        mark: p.mark ?? null,
        questionReadAt: p.questionReadAt ?? null,
        timerCompletedAt: p.timerCompletedAt ?? null,
        commentReadAt: p.commentReadAt ?? null,
        retalkCount: p.retalkCount ?? 0,
      };
    });

    const foundCount = items.filter((x) => x.mark === "found").length;
    const notFoundCount = items.filter((x) => x.mark === "not_found").length;
    const unmarkedCount = items.length - foundCount - notFoundCount;
    const resultPayload = {
      packageId,
      ownerType: selectedOwner.ownerType,
      ownerId: selectedOwner.ownerId,
      creatorUserId: currentUserId,
      startedAt: new Date().toISOString(),
      finishedAt: new Date().toISOString(),
      autoNextUsed: autoNextEnabled,
      questionCount: items.length,
      foundCount,
      notFoundCount,
      unmarkedCount,
      items,
    };

    try {
      await api.post(`packages/${packageId}/trainer/finish`, JSON.stringify({
        ownerType: selectedOwner.ownerType,
        ownerId: selectedOwner.ownerId,
        currentQuestionId: currentQuestion?.questionId ?? null,
        currentPhaseId: currentQuestion?.phaseId ?? null,
        autoNextEnabled,
        progress: buildProgressPayload(),
        result: resultPayload,
      }));
      setSummaryResult(resultPayload);
      setRequiresFinish(false);
      setStage("summary");
    } catch (err: any) {
      setErrorText(err?.response?.data?.message ?? "Sessiya tamamlanmadı.");
    } finally {
      setFinishLoading(false);
    }
  }, [autoNextEnabled, buildProgressPayload, currentQuestion, currentUserId, itemProgress, packageId, questions, selectedOwner]);

  useEffect(() => {
    if (!open) return;
    if (!isLoggedIn) return;

    let cancelled = false;
    setOwnerLoading(true);
    setErrorText(null);
    api.get("users/profile")
      .then((res) => {
        if (cancelled) return;
        const teams = Array.isArray(res?.data?.teams) ? res.data.teams : [];
        const primaryTeamId = Number(res?.data?.primaryTeamId ?? 0);

        const options: TrainerOwnerOption[] = [];
        if (currentUserId) {
          options.push({
            ownerType: "user",
            ownerId: currentUserId,
            label: "Şəxsi rejim",
          });
        }
        teams.forEach((team: any) => {
          const id = Number(team?.id ?? 0);
          if (!id) return;
          options.push({
            ownerType: "team",
            ownerId: id,
            label: `Komanda: ${team?.name ?? id}`,
          });
        });

        setOwnerOptions(options);
        const preferred = options.find((o) => o.ownerType === "team" && o.ownerId === primaryTeamId);
        const fallback = options[0];
        if (preferred) setSelectedOwnerKey(normalizeOwnerKey(preferred.ownerType, preferred.ownerId));
        else if (fallback) setSelectedOwnerKey(normalizeOwnerKey(fallback.ownerType, fallback.ownerId));
      })
      .catch(() => {
        if (cancelled) return;
        setErrorText("Profil məlumatları yüklənmədi.");
        setOwnerOptions([]);
      })
      .finally(() => {
        if (cancelled) return;
        setOwnerLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [currentUserId, isLoggedIn, open]);

  useEffect(() => {
    if (!open) return;
    if (stage !== "runner" || !currentQuestion) return;
    playQuestion(false);
  }, [currentIndex, currentQuestion, open, playQuestion, stage]);

  useEffect(() => {
    if (timerState !== "completed" || !autoNextEnabled || stage !== "runner") return;
    clearAutoAdvance();
    autoAdvanceTimeoutRef.current = window.setTimeout(() => {
      goNext();
    }, 350);
  }, [autoNextEnabled, clearAutoAdvance, goNext, stage, timerState]);

  useEffect(() => {
    if (!open || !sessionId) return;
    if (stage === "owner-select" || stage === "summary") return;

    if (autosaveTimeoutRef.current !== null) {
      window.clearTimeout(autosaveTimeoutRef.current);
    }

    autosaveTimeoutRef.current = window.setTimeout(() => {
      saveProgress();
    }, 1000);

    return () => {
      if (autosaveTimeoutRef.current !== null) {
        window.clearTimeout(autosaveTimeoutRef.current);
        autosaveTimeoutRef.current = null;
      }
    };
  }, [autoNextEnabled, currentIndex, itemProgress, open, saveProgress, sessionId, stage]);

  useEffect(() => {
    if (!open || !sessionId) return;
    if (stage === "owner-select" || stage === "summary") return;
    const interval = window.setInterval(() => {
      saveProgress();
    }, 15000);
    return () => window.clearInterval(interval);
  }, [open, saveProgress, sessionId, stage]);

  useEffect(() => {
    if (!open) return;
    return () => {
      stopSpeech();
      stopTimer();
      clearAutoAdvance();
    };
  }, [clearAutoAdvance, open, stopSpeech, stopTimer]);

  const closeOverlay = () => {
    stopSpeech();
    stopTimer();
    clearAutoAdvance();
    setSaveStatus("idle");
    setStage("owner-select");
    setPendingPhaseIndex(null);
    onClose();
  };

  const nextPhaseName = pendingPhaseIndex !== null ? questions[pendingPhaseIndex]?.phaseName : "";
  const foundCount = questions.filter((q) => itemProgress[q.questionId]?.mark === "found").length;
  const notFoundCount = questions.filter((q) => itemProgress[q.questionId]?.mark === "not_found").length;
  const unmarkedCount = questions.length - foundCount - notFoundCount;
  const displayedSummary = summaryResult ?? {
    questionCount: questions.length,
    foundCount,
    notFoundCount,
    unmarkedCount,
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[120] bg-black/50">
      <div className="absolute inset-0 flex items-center justify-center p-3 sm:p-6">
        <div className="w-full max-w-5xl max-h-[95vh] overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-2xl">
          <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 px-4 py-3 sm:px-6">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-blue-500 dark:text-indigo-300">Məşq rejimi</p>
              <h3 className="text-base font-bold text-gray-900 dark:text-gray-50">{packageName}</h3>
            </div>
            <div className="flex items-center gap-3">
              {saveStatus !== "idle" && (
                <span className={cn(
                  "text-xs font-medium",
                  saveStatus === "saving" && "text-gray-500 dark:text-gray-400",
                  saveStatus === "saved" && "text-emerald-600",
                  saveStatus === "error" && "text-red-600"
                )}>
                  {saveStatus === "saving" && "Saxlanılır..."}
                  {saveStatus === "saved" && "Saxlanıldı"}
                  {saveStatus === "error" && "Saxlama xətası"}
                </span>
              )}
              <button
                onClick={closeOverlay}
                className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Bağla
              </button>
            </div>
          </div>

          {!isLoggedIn ? (
            <div className="p-6 sm:p-10 text-center">
              <h4 className="text-lg font-bold text-gray-900 dark:text-gray-50">Trainer Mode üçün daxil olun</h4>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Sessiyanı saxlamaq və sonra davam etmək üçün hesab tələb olunur.</p>
              {isFeatureEnabled('USER_ACCOUNTS') && (
                <AuthLink
                  mode="login"
                  className="mt-5 inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                >
                  Giriş et
                </AuthLink>
              )}
            </div>
          ) : stage === "owner-select" ? (
            <div className="p-6 sm:p-8 space-y-5">
              <h4 className="text-lg font-bold text-gray-900 dark:text-gray-50">Sessiya sahibi seçin</h4>
              <p className="text-sm text-gray-500 dark:text-gray-400">Sessiya şəxsi və ya komanda adına saxlanacaq.</p>
              {ownerLoading ? (
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Yüklənir...
                </div>
              ) : ownerOptions.length === 0 ? (
                <p className="text-sm text-red-600">Sessiya sahibi tapılmadı.</p>
              ) : (
                <div className="grid gap-2">
                  {ownerOptions.map((option) => {
                    const key = normalizeOwnerKey(option.ownerType, option.ownerId);
                    const active = selectedOwnerKey === key;
                    return (
                      <button
                        key={key}
                        onClick={() => setSelectedOwnerKey(key)}
                        className={cn(
                          "rounded-xl border px-4 py-3 text-left text-sm transition-colors",
                          active ? "border-blue-500 bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-indigo-300" : "border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                        )}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              )}

              {errorText && <p className="text-sm text-red-600">{errorText}</p>}

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  onClick={closeOverlay}
                  className="rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-2 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Ləğv et
                </button>
                <button
                  onClick={() => startOrResume(false)}
                  disabled={!selectedOwner || sessionLoading || ownerLoading}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {sessionLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                  Davam et
                </button>
              </div>
            </div>
          ) : stage === "phase-pause" ? (
            <div className="p-8 sm:p-12 text-center space-y-5">
              <PauseCircle className="mx-auto h-14 w-14 text-blue-500 dark:text-indigo-300" />
              <h4 className="text-xl font-bold text-gray-900 dark:text-gray-50">Raund tamamlandı</h4>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Növbəti mərhələ: <span className="font-semibold text-gray-700 dark:text-gray-200">{nextPhaseName}</span>
              </p>
              <button
                onClick={() => {
                  if (pendingPhaseIndex !== null) {
                    setCurrentIndex(pendingPhaseIndex);
                    setPendingPhaseIndex(null);
                    setStage("runner");
                  }
                }}
                className="inline-flex items-center rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
              >
                Davam et
              </button>
            </div>
          ) : stage === "summary" ? (
            <div className="p-6 sm:p-10">
              <h4 className="text-xl font-bold text-gray-900 dark:text-gray-50">Sessiya xülasəsi</h4>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                  <p className="text-xs text-emerald-700">Tapıldı</p>
                  <p className="mt-1 text-2xl font-black text-emerald-800">{displayedSummary.foundCount ?? 0}</p>
                </div>
                <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
                  <p className="text-xs text-rose-700">Tapılmadı</p>
                  <p className="mt-1 text-2xl font-black text-rose-800">{displayedSummary.notFoundCount ?? 0}</p>
                </div>
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                  <p className="text-xs text-amber-700">İşarələnməmiş</p>
                  <p className="mt-1 text-2xl font-black text-amber-800">{displayedSummary.unmarkedCount ?? 0}</p>
                </div>
              </div>

              {errorText && <p className="mt-4 text-sm text-red-600">{errorText}</p>}

              <div className="mt-8 flex flex-wrap justify-end gap-2">
                {requiresFinish && (
                  <button
                    onClick={finishSession}
                    disabled={finishLoading}
                    className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                  >
                    {finishLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                    Finish
                  </button>
                )}
                <button
                  onClick={closeOverlay}
                  className="rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-2 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Bağla
                </button>
                <button
                  onClick={() => startOrResume(true)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50 dark:border-blue-900 dark:bg-blue-950 dark:text-indigo-300 dark:hover:bg-blue-900 dark:hover:text-indigo-200"
                >
                  <RotateCcw className="h-4 w-4" />
                  Yenidən başla
                </button>
              </div>
            </div>
          ) : (
            <div className="max-h-[calc(95vh-70px)] overflow-auto p-4 sm:p-6 space-y-4">
              {currentQuestion && (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 px-4 py-3">
                    <div>
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">{currentQuestion.phaseName}</p>
                      <p className="text-sm font-bold text-gray-900 dark:text-gray-50">
                        Sual {currentIndex + 1} / {questions.length}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className={cn(
                        "text-2xl font-black tabular-nums",
                        timerState === "completed" ? "text-emerald-600" : "text-gray-900 dark:text-gray-50"
                      )}>
                        {formatTimer(timerSeconds)}
                      </p>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400">
                        {timerState === "idle" && "Səs oxunuşundan sonra başlayır"}
                        {timerState === "running" && "Düşünmə vaxtı"}
                        {timerState === "completed" && "Vaxt tamamlandı"}
                      </p>
                    </div>
                  </div>

                  {(currentQuestion.rekvizit || currentQuestion.isRekvizitForAll) && (
                    <div className="rounded-xl border border-gray-100 dark:border-gray-800 p-3">
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">Paylama materialı</p>
                      {currentQuestion.rekvizit?.text ? (
                        <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap">{currentQuestion.rekvizit.rekvizit}</p>
                      ) : currentQuestion.rekvizit?.rekvizit ? (
                        <img
                          src={`${siteUrl}images/${currentQuestion.rekvizit.rekvizit}`}
                          alt="rekvizit"
                          className="max-h-56 rounded-lg"
                        />
                      ) : null}
                    </div>
                  )}

                  <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                    {currentQuestion.notebefore && (
                      <p className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-200">{currentQuestion.notebefore}</p>
                    )}
                    <p className="text-base font-semibold text-gray-900 dark:text-gray-50 leading-relaxed">
                      {currentQuestion.text}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="rounded-xl border border-gray-100 dark:border-gray-800 p-3">
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">Səs</p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => playQuestion(false)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          <PlayCircle className="h-4 w-4" />
                          Sualı oxu
                        </button>
                        <button
                          onClick={() => playQuestion(true)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          <Volume2 className="h-4 w-4" />
                          Yenidən oxu
                        </button>
                        <button
                          onClick={readComment}
                          disabled={!canReadComment}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40"
                        >
                          Şərhi oxu
                        </button>
                      </div>
                      {!ttsConfigured && (
                        <p className="mt-2 text-xs text-amber-700">TTSFree açarı və URL konfiqurasiyası yoxdur. Manual oxuma rejimi aktivdir.</p>
                      )}
                      {speaking && <p className="mt-2 text-xs text-blue-600 dark:text-indigo-300">Oxunur...</p>}
                      {!canReadComment && (
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Şərhi oxumaq üçün 1 dəqiqə tamamlanmalıdır.</p>
                      )}
                    </div>

                    <div className="rounded-xl border border-gray-100 dark:border-gray-800 p-3">
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">İşarələmə</p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => upsertItemProgress(currentQuestion, { mark: "found" })}
                          className={cn(
                            "inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium",
                            itemProgress[currentQuestion.questionId]?.mark === "found"
                              ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                              : "border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                          )}
                        >
                          <CheckCircle2 className="h-4 w-4" />
                          Tapdım
                        </button>
                        <button
                          onClick={() => upsertItemProgress(currentQuestion, { mark: "not_found" })}
                          className={cn(
                            "inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium",
                            itemProgress[currentQuestion.questionId]?.mark === "not_found"
                              ? "border-rose-500 bg-rose-50 text-rose-700"
                              : "border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                          )}
                        >
                          <XCircle className="h-4 w-4" />
                          Tapmadım
                        </button>
                        <button
                          onClick={() => upsertItemProgress(currentQuestion, { mark: null })}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          Təmizlə
                        </button>
                      </div>

                      <label className="mt-3 inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                        <input
                          type="checkbox"
                          checked={autoNextEnabled}
                          onChange={(e) => setAutoNextEnabled(e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 dark:border-gray-600"
                        />
                        Vaxt bitəndə avtomatik növbəti suala keç
                      </label>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
                    <button
                      onClick={goPrev}
                      disabled={currentIndex === 0}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Əvvəlki
                    </button>

                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Tapıldı: <span className="font-semibold text-emerald-700">{foundCount}</span> · Tapılmadı: <span className="font-semibold text-rose-700">{notFoundCount}</span> · Boş: <span className="font-semibold text-amber-700">{unmarkedCount}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={goNext}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                      >
                        Növbəti
                        <ChevronRight className="h-4 w-4" />
                      </button>
                      {currentIndex === questions.length - 1 && (
                        <button
                          onClick={finishSession}
                          disabled={finishLoading}
                          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                        >
                          {finishLoading ? "Tamamlanır..." : "Finish"}
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PackageTrainerOverlay;
